import { supabase } from '../lib/supabase';

export const promotionService = {
  // Get all promotions (admin only)
  async getAllPromotions() {
    const { data, error } = await supabase?.from('vetrine_promotions')?.select(`
      *,
      vetrine_stores!inner(nome_negozio, categoria, zona)
    `)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(this.convertToCamelCase) || [];
  },

  // Get active promotions (public access)
  async getActivePromotions() {
    const { data, error } = await supabase?.from('vetrine_promotions')?.select(`
      *,
      vetrine_stores!inner(nome_negozio, categoria, zona, logo_negozio)
    `)?.in('status', ['active', 'scheduled'])?.order('start_date', { ascending: false });

    if (error) throw error;

    return data?.map(this.convertToCamelCase) || [];
  },

  // Get promotions by store
  async getPromotionsByStore(storeId) {
    const { data, error } = await supabase?.from('vetrine_promotions')?.select('*')?.eq('store_id', storeId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(this.convertToCamelCase) || [];
  },

  // Get single promotion by ID
  async getPromotionById(promotionId) {
    const { data, error } = await supabase?.from('vetrine_promotions')?.select(`
      *,
      vetrine_stores!inner(nome_negozio, categoria, zona)
    `)?.eq('id', promotionId)?.single();

    if (error) throw error;

    return this.convertToCamelCase(data);
  },

  // Create new promotion (admin only)
  async createPromotion(promotionData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Non autenticato');

    const dbData = this.convertToSnakeCase(promotionData);
    dbData.created_by_admin = user?.id;
    dbData.updated_by_admin = user?.id;

    const { data, error } = await supabase?.from('vetrine_promotions')?.insert(dbData)?.select()?.single();

    if (error) throw error;

    return this.convertToCamelCase(data);
  },

  // Update promotion (admin only)
  async updatePromotion(promotionId, promotionData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Non autenticato');

    const dbData = this.convertToSnakeCase(promotionData);

    const { data, error } = await supabase?.from('vetrine_promotions')?.update(dbData)?.eq('id', promotionId)?.select()?.single();

    if (error) throw error;

    return this.convertToCamelCase(data);
  },

  // Delete promotion (admin only)
  async deletePromotion(promotionId) {
    const { error } = await supabase?.from('vetrine_promotions')?.delete()?.eq('id', promotionId);

    if (error) throw error;
  },

  // Get promotion metrics (admin only)
  async getPromotionMetrics() {
    const { data, error } = await supabase?.rpc('get_promotion_metrics');

    if (error) throw error;

    return data?.map(metric => ({
      promotionId: metric?.promotion_id,
      titolo: metric?.titolo,
      storeName: metric?.store_name,
      status: metric?.status,
      startDate: metric?.start_date,
      endDate: metric?.end_date,
      viewCount: parseInt(metric?.view_count) || 0,
      clickCount: parseInt(metric?.click_count) || 0,
      shareCount: parseInt(metric?.share_count) || 0,
      conversionCount: parseInt(metric?.conversion_count) || 0,
      engagementRate: parseFloat(metric?.engagement_rate) || 0,
      conversionRate: parseFloat(metric?.conversion_rate) || 0
    })) || [];
  },

  // Increment promotion counter
  async incrementCounter(promotionId, counterType) {
    const { error } = await supabase?.rpc('increment_promotion_counter', {
      promotion_uuid: promotionId,
      counter_type: counterType
    });

    if (error) throw error;
  },

  // Get promotion statistics summary
  async getPromotionStats() {
    const { data, error } = await supabase?.from('vetrine_promotions')?.select('status, view_count, click_count, share_count, conversion_count');

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      active: data?.filter(p => p?.status === 'active')?.length || 0,
      scheduled: data?.filter(p => p?.status === 'scheduled')?.length || 0,
      expired: data?.filter(p => p?.status === 'expired')?.length || 0,
      totalViews: data?.reduce((sum, p) => sum + (p?.view_count || 0), 0),
      totalClicks: data?.reduce((sum, p) => sum + (p?.click_count || 0), 0),
      totalShares: data?.reduce((sum, p) => sum + (p?.share_count || 0), 0),
      totalConversions: data?.reduce((sum, p) => sum + (p?.conversion_count || 0), 0)
    };

    stats.avgEngagementRate = stats?.totalViews > 0 
      ? (((stats?.totalClicks + stats?.totalShares) / stats?.totalViews) * 100)?.toFixed(2)
      : 0;

    stats.avgConversionRate = stats?.totalClicks > 0
      ? ((stats?.totalConversions / stats?.totalClicks) * 100)?.toFixed(2)
      : 0;

    return stats;
  },

  // Convert database snake_case to React camelCase
  convertToCamelCase(row) {
    if (!row) return null;

    return {
      id: row?.id,
      storeId: row?.store_id,
      titolo: row?.titolo,
      descrizione: row?.descrizione,
      discountType: row?.discount_type,
      discountValue: row?.discount_value,
      termsConditions: row?.terms_conditions,
      startDate: row?.start_date,
      endDate: row?.end_date,
      status: row?.status,
      viewCount: row?.view_count || 0,
      clickCount: row?.click_count || 0,
      shareCount: row?.share_count || 0,
      conversionCount: row?.conversion_count || 0,
      createdByAdmin: row?.created_by_admin,
      updatedByAdmin: row?.updated_by_admin,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      // Nested store data
      store: row?.vetrine_stores ? {
        nomeNegozio: row?.vetrine_stores?.nome_negozio,
        categoria: row?.vetrine_stores?.categoria,
        zona: row?.vetrine_stores?.zona,
        logoNegozio: row?.vetrine_stores?.logo_negozio
      } : null
    };
  },

  // Convert React camelCase to database snake_case
  convertToSnakeCase(data) {
    const snakeData = {};

    if (data?.storeId !== undefined) snakeData.store_id = data?.storeId;
    if (data?.titolo !== undefined) snakeData.titolo = data?.titolo;
    if (data?.descrizione !== undefined) snakeData.descrizione = data?.descrizione;
    if (data?.discountType !== undefined) snakeData.discount_type = data?.discountType;
    if (data?.discountValue !== undefined) snakeData.discount_value = data?.discountValue;
    if (data?.termsConditions !== undefined) snakeData.terms_conditions = data?.termsConditions;
    if (data?.startDate !== undefined) snakeData.start_date = data?.startDate;
    if (data?.endDate !== undefined) snakeData.end_date = data?.endDate;
    if (data?.status !== undefined) snakeData.status = data?.status;

    return snakeData;
  }
};